import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowRight, ArrowLeft, Newspaper } from "lucide-react";
import client from "@/api/client";
import { SEO } from "@/components/SEO";
import { JourneyCTA } from "@/components/JourneyCTA";
import { ART, coverFor } from "@/lib/artwork";

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang || undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogIndex() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/pages/blog", { params: { page, per_page: 12 } });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page]);
  useEffect(() => { load(); window.scrollTo({ top: 0 }); }, [load]);

  const items = data?.items || [];
  const totalPages = data?.total_pages || 1;
  const go = (p) => setSearchParams(p <= 1 ? {} : { page: String(p) });

  return (
    <>
      <SEO
        title={t("blog.title", { defaultValue: "Insights" })}
        description={t("blog.subtitle", { defaultValue: "Guides, updates and playbooks for building in Saudi Arabia." })}
        path={page > 1 ? `/blog?page=${page}` : "/blog"}
        image={ART.hero}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: t("blog.title", { defaultValue: "Insights" }),
          description: t("blog.subtitle", { defaultValue: "Guides, updates and playbooks for building in Saudi Arabia." }),
          inLanguage: isAr ? "ar" : "en",
          isPartOf: { "@type": "WebSite", name: "The Saudi Gate" },
        }}
      />

      <section className="relative overflow-hidden border-b border-border" data-testid="blog-hero">
        <img src={ART.hero} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0d1b1a]/70" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-white">
          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/70">
            <Newspaper className="h-3.5 w-3.5" /> The Saudi Gate
          </span>
          <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-2xl">
            {t("blog.title", { defaultValue: "Insights" })}
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg text-white/80 leading-relaxed">
            {t("blog.subtitle", { defaultValue: "Guides, updates and playbooks for building in Saudi Arabia." })}
          </p>
        </div>
      </section>

      <div className="relative">
        <img src={ART.soft} alt="" aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[420px] w-full object-cover opacity-45" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          {loading ? (
            <div className="flex justify-center py-20" data-testid="blog-loading">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card/70 p-12 text-center backdrop-blur" data-testid="blog-empty">
              <Newspaper className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                {t("blog.empty", { defaultValue: "No posts published yet — check back soon." })}
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
                {items.map((p, i) => {
                  const title = (isAr && p.title_ar) || p.title_en;
                  const excerpt = (isAr && (p.excerpt_ar || p.meta_desc_ar)) || p.excerpt_en || p.meta_desc_en || "";
                  return (
                    <Link
                      key={p.page_id}
                      to={`/p/${p.slug}`}
                      className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card no-underline transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg"
                      style={{ animationDelay: `${i * 45}ms` }}
                      data-testid={`blog-card-link-${p.slug}`}
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={coverFor(p.slug, p.cover_url)}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <span className="text-[11px] text-muted-foreground" data-testid={`blog-card-date-${p.slug}`}>
                          {fmtDate(p.created_at, i18n.language)}
                        </span>
                        <h2 className="font-display mt-2 text-base md:text-lg font-semibold leading-snug text-foreground group-hover:text-primary transition-colors">
                          {title}
                        </h2>
                        {excerpt && (
                          <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{excerpt}</p>
                        )}
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                          {t("blog.read", { defaultValue: "Read" })}
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl-flip" />
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <nav className="mt-12 flex flex-wrap items-center justify-center gap-2" data-testid="blog-pagination">
                  <button
                    type="button"
                    onClick={() => go(page - 1)}
                    disabled={page <= 1}
                    className="inline-flex h-10 items-center gap-1.5 rounded-full border border-border px-4 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-40"
                    data-testid="blog-page-prev"
                  >
                    {isAr ? <ArrowRight className="h-3.5 w-3.5" /> : <ArrowLeft className="h-3.5 w-3.5" />}
                    {t("blog.prev", { defaultValue: "Previous" })}
                  </button>
                  {Array.from({ length: totalPages })
                    .map((_, idx) => idx + 1)
                    .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                    .reduce((acc, n) => {
                      if (acc.length && n - acc[acc.length - 1] > 1) acc.push("gap");
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((n, i) => (n === "gap" ? (
                      <span key={`gap-${i}`} className="px-1 text-muted-foreground">…</span>
                    ) : (
                      <button
                        key={n}
                        type="button"
                        onClick={() => go(n)}
                        className={`h-10 w-10 rounded-full border text-sm font-semibold transition-colors ${
                          page === n
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border hover:bg-accent"
                        }`}
                        data-testid={`blog-page-${n}`}
                      >
                        {n}
                      </button>
                    )))}
                  <button
                    type="button"
                    onClick={() => go(page + 1)}
                    disabled={page >= totalPages}
                    className="inline-flex h-10 items-center gap-1.5 rounded-full border border-border px-4 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-40"
                    data-testid="blog-page-next"
                  >
                    {t("blog.next", { defaultValue: "Next" })}
                    {isAr ? <ArrowLeft className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
                  </button>
                </nav>
              )}
            </>
          )}

          <JourneyCTA variant="block" index={0} />
        </div>
      </div>
    </>
  );
}
